import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { CONTENT_LANGUAGE_PRESET } from "../../../utils/catalogs/contentLanguage";
import { useEnsureContentLanguageField } from "../../../hooks/useAdvertiserContentLanguage";
import { useCreditActionCosts } from "../../../hooks/useCreditActionCosts";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { getIsBlockedActions } from "../../../actions/getters";
import { getKeyDatabase } from "../../../services/public";
import { getInitialPlatformSetupData } from "../../../pages/NewAdsCampaign/utils/getInitialPlatformSetupData";
import {
  CREATE_CAMPAIGN_PHASE,
  CREATE_CAMPAIGN_REQUIRED_MESSAGE_KEY,
  DEFAULT_CREATE_CAMPAIGN_FORM,
} from "./createAdsCampaignWizardConstants.js";
import {
  canSelectCreateCampaignPlatform,
  getSetupValidationMessage,
  hasCampaignLocations,
} from "./createAdsCampaignWizardUtils.js";

import { LaunchError } from "../../../utils/errors";

const PLATFORM_ENTRY = {
  CREATE: "create",
  RESUME: "resume",
};

const getResumeFormValues = (session) => ({
  ...DEFAULT_CREATE_CAMPAIGN_FORM,
  context: session?.setupFields?.context || "",
  language: session?.setupFields?.language || "",
  locations:
    session?.setupFields?.locations || DEFAULT_CREATE_CAMPAIGN_FORM.locations,
  attachmentsFiles: session?.setupFields?.attachmentsFiles || [],
  fileNamesMapping: session?.setupFields?.fileNamesMapping || {},
  platforms: session?.platform ? [session.platform] : [],
});

export const useCreateAdsCampaignWizard = ({
  modalOpen,
  brandInformation = {},
  initialCreatives = null,
  resumeSession = null,
} = {}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );
  const resumePlatform = resumeSession?.platform || null;
  const resumeKey = resumeSession?.resumeKey || null;
  const [phase, setPhase] = useState(
    resumePlatform
      ? CREATE_CAMPAIGN_PHASE.PLATFORM
      : CREATE_CAMPAIGN_PHASE.SETUP
  );
  const [platformEntry, setPlatformEntry] = useState(
    resumePlatform ? PLATFORM_ENTRY.RESUME : PLATFORM_ENTRY.CREATE
  );
  const [selectedPlatform, setSelectedPlatform] = useState(resumePlatform);
  const [platformInitialData, setPlatformInitialData] = useState(
    resumeSession?.platformData || null
  );
  const [campaignID, setCampaignID] = useState(
    resumeSession?.campaignID || null
  );
  const [setupError, setSetupError] = useState(null);
  const [isSelectingPlatform, setIsSelectingPlatform] = useState(false);
  const galleryHydrationRef = useRef({ hydratedPlatforms: new Set() });
  const appliedResumeKeyRef = useRef(null);

  const methods = useForm({
    defaultValues: getResumeFormValues(resumeSession),
    shouldUnregister: false,
    mode: "onChange",
  });
  const { reset, setValue, getValues, watch, setError, clearErrors } = methods;
  const formState = watch();

  useEnsureContentLanguageField({
    preset: CONTENT_LANGUAGE_PRESET.ALL,
    setValue,
    getValues,
    contextLanguage: resumeSession?.setupFields?.language || null,
    enabled: modalOpen,
  });

  const resetWizard = useCallback(() => {
    reset(DEFAULT_CREATE_CAMPAIGN_FORM);
    setPhase(CREATE_CAMPAIGN_PHASE.SETUP);
    setPlatformEntry(PLATFORM_ENTRY.CREATE);
    setSelectedPlatform(null);
    setPlatformInitialData(null);
    setCampaignID(null);
    setSetupError(null);
    setIsSelectingPlatform(false);
    galleryHydrationRef.current.hydratedPlatforms.clear();
  }, [reset]);

  const applyResumeSession = useCallback(
    (session) => {
      reset(getResumeFormValues(session));
      setSetupError(null);
      setIsSelectingPlatform(false);
      setCampaignID(session.campaignID);
      setSelectedPlatform(session.platform);
      setPlatformInitialData(session.platformData || null);
      setPlatformEntry(PLATFORM_ENTRY.RESUME);
      setPhase(CREATE_CAMPAIGN_PHASE.PLATFORM);
      galleryHydrationRef.current.hydratedPlatforms.clear();
    },
    [reset]
  );

  useEffect(() => {
    if (!modalOpen) {
      appliedResumeKeyRef.current = null;
      resetWizard();
      return;
    }

    if (!resumeKey || !resumeSession?.platform) {
      if (appliedResumeKeyRef.current) {
        appliedResumeKeyRef.current = null;
        resetWizard();
      }
      return;
    }

    if (appliedResumeKeyRef.current === resumeKey) return;
    appliedResumeKeyRef.current = resumeKey;
    applyResumeSession(resumeSession);
  }, [applyResumeSession, modalOpen, resumeKey, resumeSession, resetWizard]);

  const ensureCampaignID = useCallback(() => {
    if (campaignID) {
      return campaignID;
    }
    const nextCampaignID = dispatch(getKeyDatabase());
    setCampaignID(nextCampaignID);
    return nextCampaignID;
  }, [campaignID, dispatch]);

  const onSelectPlatform = useCallback(
    async (platform) => {
      if (!platform || isSelectingPlatform) {
        return false;
      }

      const values = getValues();
      if (!canSelectCreateCampaignPlatform(values)) {
        const requiredMessage = t(CREATE_CAMPAIGN_REQUIRED_MESSAGE_KEY);
        if (!values?.language) {
          setError("language", {
            type: "required",
            message: requiredMessage,
          });
        } else {
          clearErrors("language");
        }
        if (!hasCampaignLocations(values?.locations)) {
          setError("locations", {
            type: "required",
            message: requiredMessage,
          });
        } else {
          clearErrors("locations");
        }
        setSetupError(getSetupValidationMessage(values, t));
        return false;
      }

      clearErrors(["language", "locations"]);
      setIsSelectingPlatform(true);
      setSetupError(null);

      try {
        if (isTrial) {
          const isBlocked = await dispatch(
            getIsBlockedActions({
              action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
              showUsageConfirmationAlert: false,
              campaignUsageContext: {
                platform,
                generatedPlatforms: {},
              },
            })
          );
          if (isBlocked) {
            return false;
          }
        }

        const nextCampaignID = ensureCampaignID();
        const initialData = await getInitialPlatformSetupData({
          platform,
          isFirstPlatform: true,
          brandInformation,
          initialCreatives,
          getValues,
        });

        setValue("platforms", [platform]);
        setCampaignID(nextCampaignID);
        setPlatformInitialData(initialData);
        setSelectedPlatform(platform);
        setPlatformEntry(PLATFORM_ENTRY.CREATE);
        setPhase(CREATE_CAMPAIGN_PHASE.PLATFORM);
        return true;
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useCreateAdsCampaignWizard",
            capture: "exception",
          })
        );
        setSetupError(error?.message || String(error));
        return false;
      } finally {
        setIsSelectingPlatform(false);
      }
    },
    [
      ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
      brandInformation,
      dispatch,
      ensureCampaignID,
      clearErrors,
      getValues,
      initialCreatives,
      isSelectingPlatform,
      isTrial,
      setError,
      setValue,
      t,
    ]
  );

  const onExitToPlatformSelection = useCallback(() => {
    setSetupError(null);
    setSelectedPlatform(null);
    setPlatformInitialData(null);
    setPlatformEntry(PLATFORM_ENTRY.CREATE);
    setValue("platforms", []);
    setPhase(CREATE_CAMPAIGN_PHASE.SETUP);
  }, [setValue]);

  return {
    methods,
    formState,
    phase,
    selectedPlatform,
    platformInitialData,
    resumeValues:
      platformEntry === PLATFORM_ENTRY.RESUME ? platformInitialData : null,
    campaignID,
    setupError,
    isSelectingPlatform,
    onSelectPlatform,
    onExitToPlatformSelection,
    resetWizard,
    galleryHydrationRef,
  };
};
